import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { AuthUser } from '../auth/AuthContext';
import { NavIcon } from './NavIcons';
import { PrimaryButton } from './premium';
import { isModuleEnabled } from '../lib/trackingPrefs';

type TourNavIcon = 'home' | 'nutrition' | 'workouts' | 'coach' | 'more';

type TourStep = {
  id: string;
  title: string;
  body: string;
  emoji?: string;
  icon?: TourNavIcon;
  navHint?: string;
};

function buildTourSteps(user: AuthUser): TourStep[] {
  const steps: TourStep[] = [
    {
      id: 'intro',
      emoji: '👋',
      title: 'Welcome to Peak Performance IQ',
      body: 'Here’s a quick tour of the app — about 30 seconds and you’re ready to go.',
    },
    {
      id: 'home',
      icon: 'home',
      title: 'Home',
      body: 'Your daily dashboard. See today’s calories, protein, workouts, and what to focus on next.',
      navHint: 'Center tab',
    },
  ];

  if (isModuleEnabled(user, 'nutrition')) {
    steps.push({
      id: 'nutrition',
      icon: 'nutrition',
      title: 'Nutrition',
      body: 'Log meals, track macros, and hit your calorie and protein targets.',
      navHint: 'Nutrition tab',
    });
  }

  if (isModuleEnabled(user, 'workouts')) {
    steps.push({
      id: 'workouts',
      icon: 'workouts',
      title: 'Train',
      body: 'Start workouts, follow your program, and log sets. Your history lives here too.',
      navHint: 'Train tab',
    });
  }

  steps.push({
    id: 'coach',
    icon: 'coach',
    title: 'Coach',
    body: 'Get personalized advice based on your goals, logs, and progress.',
    navHint: 'Coach tab',
  });

  steps.push({
    id: 'more',
    icon: 'more',
    title: 'More',
    body: 'Find sleep, progress, day review, profile settings, and sign out.',
    navHint: 'More tab',
  });

  if (isModuleEnabled(user, 'sleep')) {
    steps.push({
      id: 'sleep',
      emoji: '😴',
      title: 'Sleep',
      body: 'Log sleep hours from More → Sleep to keep recovery in your daily picture.',
    });
  }

  if (isModuleEnabled(user, 'goals')) {
    steps.push({
      id: 'goals',
      emoji: '📈',
      title: 'Progress',
      body: 'Track body stats and goals over time from More → Goals.',
    });
  }

  if (user.circlesEnabled) {
    steps.push({
      id: 'circles',
      emoji: '👥',
      title: 'Circles',
      body: 'Join friends, compete on plan execution, and stay accountable together.',
    });
  }

  steps.push({
    id: 'done',
    emoji: '🚀',
    title: 'You’re all set',
    body: 'Start on Home, log what you can today, and build momentum from there.',
  });

  return steps;
}

type Props = {
  user: AuthUser;
  onComplete: () => void;
};

export function WelcomeTour({ user, onComplete }: Props) {
  const steps = useMemo(() => buildTourSteps(user), [user]);
  const [index, setIndex] = useState(0);
  const step = steps[index];
  const isLast = index === steps.length - 1;

  function next() {
    if (isLast) {
      onComplete();
      return;
    }
    setIndex((i) => i + 1);
  }

  function skip() {
    onComplete();
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 p-4 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-tour-title"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-panel w-full max-w-md rounded-3xl border border-white/10 p-6 shadow-2xl"
      >
        <div className="mb-5 flex items-center justify-between gap-3">
          <div className="flex gap-1.5">
            {steps.map((s, i) => (
              <span
                key={s.id}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === index ? 'w-6 bg-sky-400' : i < index ? 'w-1.5 bg-sky-400/40' : 'w-1.5 bg-white/15'
                }`}
              />
            ))}
          </div>
          {!isLast ? (
            <button
              type="button"
              onClick={skip}
              className="shrink-0 text-xs font-semibold text-zinc-500 hover:text-zinc-300"
            >
              Skip
            </button>
          ) : null}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step.id}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/[0.06]">
              {step.icon ? (
                <NavIcon name={step.icon} filled className="h-7 w-7 text-sky-300" />
              ) : (
                <span className="text-3xl" aria-hidden>
                  {step.emoji}
                </span>
              )}
            </div>

            {step.navHint ? (
              <p className="mt-4 text-[10px] font-bold uppercase tracking-widest text-sky-400/80">{step.navHint}</p>
            ) : null}

            <h2 id="welcome-tour-title" className="mt-2 font-display text-xl font-bold text-white">
              {step.title}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">{step.body}</p>
          </motion.div>
        </AnimatePresence>

        <div className="mt-6 flex gap-2">
          {index > 0 ? (
            <button
              type="button"
              onClick={() => setIndex((i) => i - 1)}
              className="btn-press rounded-xl border border-white/10 px-4 py-3 text-sm font-semibold text-zinc-300"
            >
              Back
            </button>
          ) : null}
          <PrimaryButton fullWidth className={index > 0 ? 'flex-1' : ''} onClick={next}>
            {isLast ? 'Get started' : 'Next'}
          </PrimaryButton>
        </div>

        <p className="mt-3 text-center text-[11px] text-zinc-600">
          Step {index + 1} of {steps.length}
        </p>
      </motion.div>
    </div>
  );
}
